import { useCallback, useEffect, useRef, useState } from 'react';
import { Geolocation } from '@capacitor/geolocation';

import { ILatLng } from 'common/geo';
import useLocalStorage from './useLocalStorage';

export type UserLocationStatus = 'idle' | 'locating' | 'located' | 'denied' | 'unavailable';

// Remembers that the user already shared their location, so later visits can
// re-locate without making them tap the button again (no OS prompt fires once
// permission is granted).
const OPT_IN_KEY = 'nearbyCasinos.locationOptIn';

const PERMISSION_DENIED = 1; // GeolocationPositionError.PERMISSION_DENIED

const isPermissionDenied = (error: unknown): boolean => {
  const { code, message } = (error ?? {}) as { code?: unknown; message?: unknown };
  // Web rejects with a GeolocationPositionError; the native plugin rejects
  // with "Location permission was denied" style messages.
  return code === PERMISSION_DENIED || (typeof message === 'string' && /denied/i.test(message));
};

/**
 * Asks for the device location only when `requestLocation` is called (or
 * automatically on mount if the user opted in on a previous visit). When
 * `enabled` is false it never touches the location API.
 */
export const useUserLocation = (enabled = true) => {
  const [hasOptedIn, setHasOptedIn] = useLocalStorage<boolean>(OPT_IN_KEY, false);
  const [status, setStatus] = useState<UserLocationStatus>('idle');
  const [coords, setCoords] = useState<ILatLng | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const requestLocation = useCallback(async () => {
    setStatus('locating');
    try {
      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: false,
        timeout: 15000,
        maximumAge: 5 * 60 * 1000,
      });
      if (!isMountedRef.current) {
        return;
      }
      setCoords({ lat: position.coords.latitude, lng: position.coords.longitude });
      setStatus('located');
      setHasOptedIn(true);
    } catch (error) {
      if (!isMountedRef.current) {
        return;
      }
      const denied = isPermissionDenied(error);
      setStatus(denied ? 'denied' : 'unavailable');
      if (denied) {
        setHasOptedIn(false);
      }
    }
    // setHasOptedIn is recreated each render by useLocalStorage; it only writes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (enabled && hasOptedIn) {
      requestLocation().catch(() => null);
    }
    // Only on mount: a fresh opt-in is already located by the button tap.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { coords, status, requestLocation };
};
